/**
 * Payment Provider Configuration
 * Controls which payment provider to use based on environment variables
 */

export interface PaymentConfig {
  enableStripe: boolean;
  enableAuthorizeNet: boolean;
  defaultProvider: 'stripe' | 'authorizeNet';
}

/**
 * Get payment configuration based on environment variables
 */
export function getPaymentConfig(): PaymentConfig {
  const enableStripe = process.env.REACT_APP_ENABLE_STRIPE === 'true';
  const enableAuthorizeNet = process.env.REACT_APP_ENABLE_AUTHORIZE_NET !== 'false'; // Default to true if not specified
  
  // Debug logging
  console.log('Payment Config Debug:', {
    REACT_APP_ENABLE_STRIPE: process.env.REACT_APP_ENABLE_STRIPE,
    enableStripe,
    enableAuthorizeNet,
    defaultProvider: enableStripe ? 'stripe' : 'authorizeNet'
  });
  
  return {
    enableStripe,
    enableAuthorizeNet,
    defaultProvider: enableStripe ? 'stripe' : 'authorizeNet',
  };
}

/**
 * Check if Stripe is enabled
 */
export function isStripeEnabled(): boolean {
  return getPaymentConfig().enableStripe;
}

/**
 * Check if Authorize.net is enabled
 */
export function isAuthorizeNetEnabled(): boolean {
  return getPaymentConfig().enableAuthorizeNet;
}

/**
 * Get the default payment provider
 */
export function getDefaultPaymentProvider(): 'stripe' | 'authorizeNet' {
  return getPaymentConfig().defaultProvider;
}

/**
 * Check if order uses Stripe payment
 */
export function isStripeOrder(order: any): boolean {
  return !!(order?.stripePaymentIntentId || order?.stripeCheckoutSessionId);
}

/**
 * Check if order uses Authorize.net payment
 */
export function isAuthorizeNetOrder(order: any): boolean {
  return !!(order?.transactionId && !order?.stripePaymentIntentId);
}

/**
 * Get payment provider for an order
 */
export function getOrderPaymentProvider(order: any): 'stripe' | 'authorizeNet' | 'unknown' {
  if (isStripeOrder(order)) {
    return 'stripe';
  }
  if (isAuthorizeNetOrder(order)) {
    return 'authorizeNet';
  }
  return 'unknown';
} 